// ─── Stock Adjustment Conflict View ──────────────────────────────────────────
//
// Emerald accent to match ENTITY_TYPE_CONFIG.STOCK_ADJUSTMENT.tone = 'synced'.
// Traceability: FR-SYNC-013 (conflict resolution), FR-SYNC-014.

import { STOCK_ADJUSTMENT_SCHEMA, formatValue } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import { GroupCard } from './GroupCard';
import { ConflictHeader } from './ConflictHeader';

export interface StockAdjustmentConflictViewProps {
  clientVersion: Record<string, unknown>;
  serverVersion: Record<string, unknown>;
  deviceId: string | null;
  conflictMessage?: string;
  /**
   * Tenant currency for the Unit Cost field. Defaults to `'INR'`
   * (byte-identical); non-INR threads the correct symbol/subunit/decimals.
   */
  currency?: CurrencyCode;
}

export function StockAdjustmentConflictView({
  clientVersion,
  serverVersion,
  deviceId,
  conflictMessage,
  currency = 'INR',
}: StockAdjustmentConflictViewProps) {
  const adjustmentType =
    (clientVersion.type as string | undefined) ??
    (serverVersion.type as string | undefined);

  const productName =
    (clientVersion.name as string | undefined) ??
    (serverVersion.name as string | undefined) ??
    (clientVersion.product_id as string | undefined) ??
    'Unknown';

  return (
    <div className="space-y-4 animate-fade-in" data-testid="stock-adjustment-conflict-view">
      <ConflictHeader
        entityLabel="Stock Adjustment Conflict"
        identifier={productName}
        accent={{
          from: 'from-emerald-500/10',
          to: 'to-teal-500/10',
          ring: 'ring-emerald-500/20',
          text: 'text-emerald-700',
          darkText: 'dark:text-emerald-300',
        }}
        tone="teal"
        badgeVariant="synced"
        badgeLabel={adjustmentType ? formatValue('enum', adjustmentType) : undefined}
        deviceId={deviceId}
        conflictMessage={conflictMessage}
        ariaLabel="Stock adjustment conflict header"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {STOCK_ADJUSTMENT_SCHEMA.groups.map((group, i) => (
          <GroupCard
            key={group.title}
            title={group.title}
            fields={group.fields}
            clientVersion={clientVersion}
            serverVersion={serverVersion}
            currency={currency}
            delay={i * 60}
          />
        ))}
      </div>
    </div>
  );
}
